import type { RecognitionFrame } from 'AudioRecognition/types';
import { hzToMidi, midiToName } from 'AudioRecognition/util/note';
import {
  evaluateRecognitionReplayFrames,
  parseRecognitionExpectedNotes,
  type RecognitionReplayEvaluation,
  type RecognitionReplayNoteSource,
} from 'AudioRecognition/testing/recognitionReplay';
import {
  getAudioCaptureScore,
  normalizeMidiNotes,
  type AudioCaptureCandidate,
} from './audioCapture';

export const AUDIO_DIAGNOSTICS_MAX_FRAMES = 240;
export const AUDIO_DIAGNOSTICS_TOP_PEAKS = 10;
export const AUDIO_DIAGNOSTICS_UI_INTERVAL_MS = 120;
export const AUDIO_DIAGNOSTICS_WAVEFORM_SAMPLES = 128;

export { parseRecognitionExpectedNotes };

export type AudioDiagnosticPeak = {
  hz: number;
  midi: number;
  note: string;
  amp: number;
  score?: number;
};

export type AudioDiagnosticDetectedNote = {
  midi: number;
  note: string;
  hz: number;
  score: number;
  energy: number;
  normalizedEnergy: number;
  expected: boolean;
  onset: boolean;
};

export type AudioDiagnosticCaptureCandidate = AudioCaptureCandidate & {
  note: string;
  captureScore: number;
};

export type AudioDiagnosticSuppressedNote = {
  midi: number;
  note: string;
  score: number;
  normalizedEnergy: number;
  reason: string;
  suppressedByMidi?: number;
  suppressedByNote?: string;
  confidence: number;
};

export type AudioDiagnosticsFrame = {
  capturedAtMs: number;
  capturedAtIso: string;
  timeSec: number;
  rms: number;
  minRms: number;
  noiseFloor: number;
  midiNotes: number[];
  onsetMidiNotes: number[];
  peaks: AudioDiagnosticPeak[];
  detectedNotes: AudioDiagnosticDetectedNote[];
  selectedNotes: AudioDiagnosticDetectedNote[];
  suppressedNotes: AudioDiagnosticSuppressedNote[];
  captureCandidates: AudioDiagnosticCaptureCandidate[];
  lastCaptureCandidates: AudioDiagnosticCaptureCandidate[];
  residualEnergy: number | null;
  recognitionMode: string;
  waveform?: number[];
};

export type AudioDiagnosticsPeakSummary = {
  hz: number;
  midi: number;
  note: string;
  maxAmp: number;
  avgAmp: number;
  count: number;
};

export type AudioDiagnosticsSnapshot = AudioDiagnosticsFrame & {
  rollingFrameCount: number;
  rollingMs: number;
  peakSummary: AudioDiagnosticsPeakSummary[];
};

export type AudioDiagnosticsSessionEvaluationSet = Record<
  RecognitionReplayNoteSource,
  RecognitionReplayEvaluation
>;

export type AudioDiagnosticsSession = {
  schemaVersion: 1;
  source: string;
  createdAtIso: string;
  label: string;
  expectedMidiNotes: number[];
  expectedNoteNames: string[];
  frameCount: number;
  durationMs: number;
  recognitionModes: string[];
  peakSummary: AudioDiagnosticsPeakSummary[];
  frames: AudioDiagnosticsFrame[];
  evaluations: AudioDiagnosticsSessionEvaluationSet | null;
  metadata?: Record<string, unknown>;
};

type BuildAudioDiagnosticsSessionOptions = {
  source?: string;
  label?: string;
  expectedMidiNotes?: number[];
  metadata?: Record<string, unknown>;
  createdAtMs?: number;
};

const noteNameFromHz = (hz: number) => midiToName(Math.round(hzToMidi(hz)));

const toAudioDiagnosticPeak = (peak: NonNullable<RecognitionFrame['peaks']>[number]) => {
  const midi = Math.round(hzToMidi(peak.hz));
  return {
    hz: peak.hz,
    midi,
    note: noteNameFromHz(peak.hz),
    amp: peak.amp,
    score: peak.score,
  };
};

const getTopAudioDiagnosticPeaks = (frame: RecognitionFrame) =>
  (frame.peaks ?? [])
    .filter((peak) => Number.isFinite(peak.hz) && peak.hz > 0 && Number.isFinite(peak.amp))
    .map(toAudioDiagnosticPeak)
    .sort((a, b) => b.amp - a.amp || a.hz - b.hz)
    .slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS);

const serializeWaveform = (waveform: Float32Array | undefined) => {
  if (!waveform || waveform.length === 0) return undefined;

  const stride = Math.max(1, Math.ceil(waveform.length / AUDIO_DIAGNOSTICS_WAVEFORM_SAMPLES));
  const serialized: number[] = [];
  for (let index = 0; index < waveform.length; index += stride) {
    serialized.push(Number(waveform[index].toFixed(5)));
    if (serialized.length >= AUDIO_DIAGNOSTICS_WAVEFORM_SAMPLES) break;
  }
  return serialized;
};

export const buildAudioDiagnosticsFrame = (
  frame: RecognitionFrame,
  captureCandidates: AudioCaptureCandidate[],
  lastCaptureCandidates: AudioCaptureCandidate[] = [],
  capturedAtMs = Date.now(),
): AudioDiagnosticsFrame => ({
  capturedAtMs,
  capturedAtIso: new Date(capturedAtMs).toISOString(),
  timeSec: frame.timeSec,
  rms: frame.rms ?? 0,
  minRms: frame.minRms ?? 0,
  noiseFloor: frame.noiseFloor ?? 0,
  midiNotes: normalizeMidiNotes(frame.midiNotes ?? []),
  onsetMidiNotes: normalizeMidiNotes(frame.onsetMidiNotes ?? []),
  peaks: getTopAudioDiagnosticPeaks(frame),
  detectedNotes: (frame.detectedNotes ?? [])
    .map((note) => ({
      midi: Math.round(note.midi),
      note: note.note,
      hz: note.hz,
      score: note.score,
      energy: note.energy,
      normalizedEnergy: note.normalizedEnergy,
      expected: note.expected,
      onset: note.onset,
    }))
    .sort((a, b) => b.normalizedEnergy - a.normalizedEnergy || b.score - a.score || a.midi - b.midi)
    .slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS),
  selectedNotes: (frame.selectedNotes ?? [])
    .map((note) => ({
      midi: Math.round(note.midi),
      note: note.note,
      hz: note.hz,
      score: note.score,
      energy: note.energy,
      normalizedEnergy: note.normalizedEnergy,
      expected: note.expected,
      onset: note.onset,
    }))
    .sort((a, b) => b.normalizedEnergy - a.normalizedEnergy || b.score - a.score || a.midi - b.midi)
    .slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS),
  suppressedNotes: (frame.suppressedNotes ?? [])
    .map((note) => ({
      midi: Math.round(note.midi),
      note: note.note,
      score: note.score,
      normalizedEnergy: note.normalizedEnergy,
      reason: note.reason,
      suppressedByMidi: note.suppressedByMidi,
      suppressedByNote:
        note.suppressedByMidi == null ? undefined : midiToName(note.suppressedByMidi),
      confidence: note.confidence,
    }))
    .sort((a, b) => b.normalizedEnergy - a.normalizedEnergy || a.midi - b.midi)
    .slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS),
  captureCandidates: captureCandidates
    .map((candidate) => ({
      ...candidate,
      note: midiToName(candidate.midi),
      captureScore: getAudioCaptureScore(candidate),
    }))
    .sort((a, b) => b.captureScore - a.captureScore || a.midi - b.midi)
    .slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS),
  lastCaptureCandidates: lastCaptureCandidates
    .map((candidate) => ({
      ...candidate,
      note: midiToName(candidate.midi),
      captureScore: getAudioCaptureScore(candidate),
    }))
    .sort((a, b) => b.captureScore - a.captureScore || a.midi - b.midi)
    .slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS),
  residualEnergy: frame.residualEnergy ?? null,
  recognitionMode: frame.recognitionMode ?? 'default',
  waveform: serializeWaveform(frame.waveform),
});

export const buildAudioDiagnosticsPeakSummary = (
  frames: AudioDiagnosticsFrame[],
): AudioDiagnosticsPeakSummary[] => {
  const grouped = new Map<
    number,
    {
      midi: number;
      note: string;
      hzAtMaxAmp: number;
      maxAmp: number;
      ampSum: number;
      count: number;
    }
  >();

  frames.forEach((frame) => {
    frame.peaks.forEach((peak) => {
      const existing = grouped.get(peak.midi);
      if (!existing) {
        grouped.set(peak.midi, {
          midi: peak.midi,
          note: peak.note,
          hzAtMaxAmp: peak.hz,
          maxAmp: peak.amp,
          ampSum: peak.amp,
          count: 1,
        });
        return;
      }

      existing.ampSum += peak.amp;
      existing.count += 1;
      if (peak.amp > existing.maxAmp) {
        existing.maxAmp = peak.amp;
        existing.hzAtMaxAmp = peak.hz;
      }
    });
  });

  return Array.from(grouped.values())
    .map((group) => ({
      hz: group.hzAtMaxAmp,
      midi: group.midi,
      note: group.note,
      maxAmp: group.maxAmp,
      avgAmp: group.ampSum / Math.max(group.count, 1),
      count: group.count,
    }))
    .sort((a, b) => b.maxAmp - a.maxAmp || a.midi - b.midi)
    .slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS);
};

export const buildAudioDiagnosticsSnapshot = (
  frames: AudioDiagnosticsFrame[],
): AudioDiagnosticsSnapshot | null => {
  const latestFrame = frames[frames.length - 1];
  if (!latestFrame) return null;

  const firstFrame = frames[0] ?? latestFrame;
  return {
    ...latestFrame,
    rollingFrameCount: frames.length,
    rollingMs: Math.max(0, latestFrame.capturedAtMs - firstFrame.capturedAtMs),
    peakSummary: buildAudioDiagnosticsPeakSummary(frames),
  };
};

export const buildAudioDiagnosticsSession = (
  frames: AudioDiagnosticsFrame[],
  options: BuildAudioDiagnosticsSessionOptions = {},
): AudioDiagnosticsSession | null => {
  if (frames.length === 0) return null;

  const firstFrame = frames[0];
  const latestFrame = frames[frames.length - 1];
  const expectedMidiNotes = normalizeMidiNotes(options.expectedMidiNotes ?? []);
  const recognitionModes = Array.from(new Set(frames.map((frame) => frame.recognitionMode))).sort();
  const replayFrames = frames.map((frame) => ({
    capturedAtMs: frame.capturedAtMs,
    midiNotes: frame.midiNotes,
    onsetMidiNotes: frame.onsetMidiNotes,
    selectedMidiNotes: frame.selectedNotes.map((note) => note.midi),
    captureMidiNotes: frame.captureCandidates.map((candidate) => candidate.midi),
    residualEnergy: frame.residualEnergy,
  }));
  const evaluations =
    expectedMidiNotes.length > 0
      ? {
          capture: evaluateRecognitionReplayFrames(replayFrames, expectedMidiNotes, 'capture'),
          selected: evaluateRecognitionReplayFrames(replayFrames, expectedMidiNotes, 'selected'),
          stable: evaluateRecognitionReplayFrames(replayFrames, expectedMidiNotes, 'stable'),
          onset: evaluateRecognitionReplayFrames(replayFrames, expectedMidiNotes, 'onset'),
        }
      : null;

  return {
    schemaVersion: 1,
    source: options.source ?? 'repertoire-create',
    createdAtIso: new Date(options.createdAtMs ?? Date.now()).toISOString(),
    label: options.label?.trim() || 'Unlabeled audio recognition capture',
    expectedMidiNotes,
    expectedNoteNames: expectedMidiNotes.map(midiToName),
    frameCount: frames.length,
    durationMs: Math.max(0, latestFrame.capturedAtMs - firstFrame.capturedAtMs),
    recognitionModes,
    peakSummary: buildAudioDiagnosticsPeakSummary(frames),
    frames,
    evaluations,
    metadata: options.metadata,
  };
};

export const formatDiagnosticNumber = (value: number, fractionDigits = 4) =>
  Number.isFinite(value) ? value.toFixed(fractionDigits) : 'n/a';

export const formatDiagnosticPercent = (value: number, fractionDigits = 0) =>
  Number.isFinite(value) ? `${(value * 100).toFixed(fractionDigits)}%` : 'n/a';

export const formatDiagnosticNotes = (midiNotes: number[]) =>
  midiNotes.length > 0
    ? midiNotes.map((midi) => `${midiToName(midi)} (${midi})`).join(', ')
    : 'none';

export const formatAudioDiagnosticsClipboard = (snapshot: AudioDiagnosticsSnapshot) => {
  const lines = [
    'Audio recognition diagnostics',
    `Captured at: ${snapshot.capturedAtIso}`,
    `Frame time: ${formatDiagnosticNumber(snapshot.timeSec, 3)}s`,
    `RMS: ${formatDiagnosticNumber(snapshot.rms, 6)}; minRMS: ${formatDiagnosticNumber(
      snapshot.minRms,
      6,
    )}; noiseFloor: ${formatDiagnosticNumber(snapshot.noiseFloor, 6)}`,
    `Stable MIDI notes: ${formatDiagnosticNotes(snapshot.midiNotes)}`,
    `Onset MIDI notes: ${formatDiagnosticNotes(snapshot.onsetMidiNotes)}`,
    `Recognition mode: ${snapshot.recognitionMode}; residual energy: ${
      snapshot.residualEnergy == null ? 'n/a' : formatDiagnosticNumber(snapshot.residualEnergy, 4)
    }`,
    '',
    'Latest top frequency peaks:',
    ...snapshot.peaks.map(
      (peak, index) =>
        `${index + 1}. ${peak.note} midi=${peak.midi} hz=${formatDiagnosticNumber(
          peak.hz,
          2,
        )} amp=${formatDiagnosticNumber(peak.amp, 6)} score=${formatDiagnosticNumber(
          peak.score ?? peak.amp,
          6,
        )}`,
    ),
    '',
    `Rolling peak summary (${snapshot.rollingFrameCount} frames, ${Math.round(
      snapshot.rollingMs,
    )}ms):`,
    ...snapshot.peakSummary.map(
      (peak, index) =>
        `${index + 1}. ${peak.note} midi=${peak.midi} hz=${formatDiagnosticNumber(
          peak.hz,
          2,
        )} maxAmp=${formatDiagnosticNumber(peak.maxAmp, 6)} avgAmp=${formatDiagnosticNumber(
          peak.avgAmp,
          6,
        )} frames=${peak.count}`,
    ),
    '',
    'Detector stable notes:',
    ...snapshot.detectedNotes.map(
      (note, index) =>
        `${index + 1}. ${note.note} midi=${note.midi} hz=${formatDiagnosticNumber(
          note.hz,
          2,
        )} score=${formatDiagnosticNumber(note.score, 4)} energy=${formatDiagnosticNumber(
          note.energy,
          4,
        )} normalized=${formatDiagnosticNumber(note.normalizedEnergy, 4)} onset=${note.onset}`,
    ),
    '',
    'Sparse selected notes:',
    ...snapshot.selectedNotes.map(
      (note, index) =>
        `${index + 1}. ${note.note} midi=${note.midi} score=${formatDiagnosticNumber(
          note.score,
          4,
        )} normalized=${formatDiagnosticNumber(note.normalizedEnergy, 4)}`,
    ),
    '',
    'Suppressed aliases/candidates:',
    ...snapshot.suppressedNotes.map(
      (note, index) =>
        `${index + 1}. ${note.note} midi=${note.midi} reason=${note.reason}${
          note.suppressedByNote ? ` by=${note.suppressedByNote}` : ''
        } confidence=${formatDiagnosticNumber(note.confidence, 4)} normalized=${formatDiagnosticNumber(
          note.normalizedEnergy,
          4,
        )}`,
    ),
    '',
    'Create capture candidates:',
    ...snapshot.captureCandidates.map(
      (candidate, index) =>
        `${index + 1}. ${candidate.note} midi=${candidate.midi} captureScore=${formatDiagnosticNumber(
          candidate.captureScore,
          4,
        )} score=${formatDiagnosticNumber(candidate.score, 4)} energy=${formatDiagnosticNumber(
          candidate.energy,
          4,
        )} normalized=${formatDiagnosticNumber(candidate.normalizedEnergy, 4)}`,
    ),
    '',
    'Last captured onset candidates:',
    ...snapshot.lastCaptureCandidates.map(
      (candidate, index) =>
        `${index + 1}. ${candidate.note} midi=${candidate.midi} captureScore=${formatDiagnosticNumber(
          candidate.captureScore,
          4,
        )} score=${formatDiagnosticNumber(candidate.score, 4)} normalized=${formatDiagnosticNumber(
          candidate.normalizedEnergy,
          4,
        )}`,
    ),
    '',
    'JSON:',
    JSON.stringify(snapshot, null, 2),
  ];

  return lines.join('\n');
};

const escapeXml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export const buildAudioDiagnosticsScreenshotSvg = (snapshot: AudioDiagnosticsSnapshot) => {
  const width = 920;
  const height = 560;
  const chartLeft = 44;
  const chartTop = 96;
  const chartWidth = 820;
  const chartHeight = 210;
  const barGap = 9;
  const peaks = snapshot.peaks.slice(0, AUDIO_DIAGNOSTICS_TOP_PEAKS);
  const maxAmp = Math.max(...peaks.map((peak) => peak.amp), 0.000001);
  const barWidth = peaks.length > 0 ? (chartWidth - barGap * (peaks.length - 1)) / peaks.length : 0;
  const bars = peaks
    .map((peak, index) => {
      const heightRatio = Math.max(0.02, Math.min(1, peak.amp / maxAmp));
      const barHeight = chartHeight * heightRatio;
      const x = chartLeft + index * (barWidth + barGap);
      const y = chartTop + chartHeight - barHeight;
      return [
        `<rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" rx="4" fill="#6F87F1"/>`,
        `<text x="${x + barWidth / 2}" y="${chartTop + chartHeight + 22}" text-anchor="middle" font-size="13" fill="#111827" font-weight="700">${escapeXml(
          peak.note,
        )}</text>`,
        `<text x="${x + barWidth / 2}" y="${chartTop + chartHeight + 40}" text-anchor="middle" font-size="11" fill="#4B5563">${formatDiagnosticNumber(
          peak.hz,
          1,
        )}Hz</text>`,
      ].join('');
    })
    .join('');
  const summaryRows = snapshot.peakSummary
    .slice(0, 5)
    .map(
      (peak, index) =>
        `<text x="44" y="${382 + index * 24}" font-size="14" fill="#111827">${index + 1}. ${escapeXml(
          peak.note,
        )} midi=${peak.midi} hz=${formatDiagnosticNumber(peak.hz, 2)} maxAmp=${formatDiagnosticNumber(
          peak.maxAmp,
          6,
        )} avgAmp=${formatDiagnosticNumber(peak.avgAmp, 6)} frames=${peak.count}</text>`,
    )
    .join('');
  const detectedRows = snapshot.detectedNotes
    .slice(0, 4)
    .map(
      (note, index) =>
        `<text x="500" y="${382 + index * 24}" font-size="14" fill="#111827">${index + 1}. ${escapeXml(
          note.note,
        )} midi=${note.midi} score=${formatDiagnosticNumber(note.score, 4)} norm=${formatDiagnosticNumber(
          note.normalizedEnergy,
          4,
        )} onset=${note.onset}</text>`,
    )
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <rect width="100%" height="100%" fill="#F7E8CD"/>
    <text x="44" y="44" font-size="24" font-weight="800" fill="#111827">Audio recognition diagnostics</text>
    <text x="44" y="70" font-size="13" fill="#4B5563">${escapeXml(snapshot.capturedAtIso)} · RMS ${formatDiagnosticNumber(
      snapshot.rms,
      6,
    )} · minRMS ${formatDiagnosticNumber(snapshot.minRms, 6)} · stable ${escapeXml(
      formatDiagnosticNotes(snapshot.midiNotes),
    )}</text>
    <line x1="${chartLeft}" y1="${chartTop + chartHeight}" x2="${
      chartLeft + chartWidth
    }" y2="${chartTop + chartHeight}" stroke="#111827" stroke-opacity="0.28"/>
    ${bars}
    <text x="44" y="350" font-size="16" font-weight="800" fill="#111827">Rolling top peaks</text>
    <text x="500" y="350" font-size="16" font-weight="800" fill="#111827">Detector notes</text>
    ${summaryRows}
    ${detectedRows}
  </svg>`;
};

export const svgToPngBlob = (svg: string) =>
  new Promise<Blob>((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d');
      if (!context) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not create screenshot canvas.'));
        return;
      }
      context.drawImage(image, 0, 0);
      canvas.toBlob((pngBlob) => {
        URL.revokeObjectURL(url);
        if (pngBlob) {
          resolve(pngBlob);
        } else {
          reject(new Error('Could not encode screenshot.'));
        }
      }, 'image/png');
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not render screenshot.'));
    };
    image.src = url;
  });

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const copyTextWithFallback = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
};
